import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, money, priceView } from "@/lib/api";
import { Package, Tag, Users, ShoppingCart, DollarSign, Truck, Plus, Pencil, Copy, Archive, ShieldOff, AlertTriangle, Images } from "lucide-react";
import { toast } from "sonner";
import { EmailLoginForm } from "@/components/store/EmailLoginForm";
import { InventoryTab } from "@/components/admin/InventoryTab";
import { InventoryHistoryTab } from "@/components/admin/InventoryHistoryTab";
import { InventoryImportExportTab } from "@/components/admin/InventoryImportExportTab";
import { InventoryDashboard } from "@/components/admin/InventoryDashboard";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { CustomersTab } from "@/components/admin/CustomersTab";
import { OrdersTab } from "@/components/admin/OrdersTab";
import { ContentTab } from "@/components/admin/ContentTab";
import { PromoLinesTab } from "@/components/admin/PromoLinesTab";
import { StoreGalleryTab } from "@/components/admin/StoreGalleryTab";
import { PickupHoursTab } from "@/components/admin/PickupHoursTab";
import { HoldsTab } from "@/components/admin/HoldsTab";
import { ProductImagesModal } from "@/components/admin/ProductImagesModal";
import { POSDashboardTab, SalesTab, StaffTab, POSSettingsTab, DangerZoneTab } from "@/components/admin/POSAdmin";
import { POSProductIO } from "@/components/admin/POSProductIO";
import { AdjustInventoryModal, InventoryHistoryModal, StatusPill } from "@/components/admin/InventoryShared";

const TABS = ["Dashboard", "Prepare Order", "Sales", "Inventory", "Import/Export", "Prepare Order Products", "History", "Products", "Categories", "Promo Lines", "Gallery", "Pickup Hours", "Holds", "Staff", "Customers", "Orders", "Content", "Prepare Order Settings", "Settings"];

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("Dashboard");
  const [inventoryFlag, setInventoryFlag] = useState("");
  const goInventory = (flag = "") => { setInventoryFlag(flag); setTab("Inventory"); };

  useEffect(() => { document.title = "Admin · Dealswagon"; }, []);

  if (loading) return <div className="p-10">Loading...</div>;

  if (!user) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        <ShieldOff className="h-12 w-12 mx-auto text-slate-300" />
        <h1 className="font-heading text-2xl text-brand-navy mt-4">Admin sign-in required</h1>
        <div className="mt-6"><EmailLoginForm /></div>
        <div className="my-4 text-xs uppercase tracking-widest text-slate-400 font-bold">or</div>
        <button onClick={login} data-testid="admin-signin-btn"
          className="bg-brand-navy text-white rounded-full h-12 px-6 font-bold">Continue with Google</button>
      </main>
    );
  }

  if (!["SUPER_ADMIN", "ADMIN"].includes(user.role)) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center" data-testid="admin-forbidden">
        <ShieldOff className="h-12 w-12 mx-auto text-brand-red" />
        <h1 className="font-heading text-2xl text-brand-navy mt-4">Not authorized</h1>
        <p className="text-slate-600 mt-2">Signed in as {user.email}. Admin access is limited to Dealswagon staff.</p>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-brand-red font-black uppercase tracking-widest text-xs">Dealswagon Admin</div>
            <div className="font-heading text-xl text-brand-navy">Control Center</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/pos" data-testid="open-pos-btn" className="inline-flex items-center gap-2 bg-brand-red text-white rounded-full h-10 px-5 font-bold text-sm hover:bg-brand-redHover">Open Prepare Order</a>
            <div className="text-sm text-slate-500">Signed in as <span className="font-bold text-slate-800">{user.email}</span></div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-3 flex gap-1 overflow-x-auto">
          {[...TABS, ...(user.role === "SUPER_ADMIN" ? ["Danger Zone"] : [])].map((t) => (
            <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/[\s/]+/g, "-")}`} onClick={() => setTab(t)}
              className={`px-3 py-2 rounded-full text-sm font-bold whitespace-nowrap ${tab === t ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === "Dashboard" && <Dashboard onViewInventory={goInventory} />}
        {tab === "Prepare Order" && <POSDashboardTab />}
        {tab === "Sales" && <SalesTab />}
        {tab === "Staff" && <StaffTab />}
        {tab === "Prepare Order Products" && <POSProductIO />}
        {tab === "Prepare Order Settings" && <POSSettingsTab />}
        {tab === "Inventory" && <InventoryTab key={inventoryFlag} initialFlag={inventoryFlag} />}
        {tab === "Import/Export" && <InventoryImportExportTab />}
        {tab === "History" && <InventoryHistoryTab />}
        {tab === "Products" && <Products />}
        {tab === "Categories" && <Categories />}
        {tab === "Promo Lines" && <PromoLinesTab />}
        {tab === "Gallery" && <StoreGalleryTab />}
        {tab === "Pickup Hours" && <PickupHoursTab />}
        {tab === "Holds" && <HoldsTab />}
        {tab === "Customers" && <CustomersTab />}
        {tab === "Orders" && <OrdersTab />}
        {tab === "Content" && <ContentTab />}
        {tab === "Settings" && <SettingsTab />}
        {tab === "Danger Zone" && user.role === "SUPER_ADMIN" && <DangerZoneTab />}
      </div>
    </div>
  );
}

const Dashboard = ({ onViewInventory }) => {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)); }, []);
  const cards = [
    { icon: DollarSign, label: "Sales", value: "—", note: "Coming with payments" },
    { icon: ShoppingCart, label: "Orders", value: m?.orders ?? 0, note: "Coming with checkout" },
    { icon: Tag, label: "Categories", value: m?.categories ?? 0 },
    { icon: Truck, label: "Pickups", value: m?.pickups ?? 0, note: "Coming with pickup slots" },
    { icon: Users, label: "Customers", value: m?.customers ?? 0 },
    { icon: Users, label: "Newsletter", value: m?.newsletter ?? 0 },
  ];
  return (
    <div className="space-y-6">
      <InventoryDashboard onViewInventory={onViewInventory} />
      <div className="font-heading text-lg text-brand-navy">Store</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(({ icon: Icon, label, value, note }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`admin-metric-${label.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <Icon className="h-5 w-5 text-brand-red" />
              <div className="text-xs text-slate-400">{note}</div>
            </div>
            <div className="mt-3 font-heading text-3xl text-brand-navy">{value}</div>
            <div className="text-sm text-slate-500 font-bold uppercase tracking-wider">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [imagesFor, setImagesFor] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, []);

  const duplicate = async (id) => { await api.post(`/admin/products/${id}/duplicate`); toast.success("Duplicated"); load(); };
  const archive = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Archived"); load(); };

  const term = search.trim().toLowerCase();
  const shown = term
    ? products.filter((p) => {
        const catName = cats.find((c) => c.id === p.category_id)?.name || "";
        return [p.product_name, p.sku, p.barcode, catName].some((v) => (v || "").toLowerCase().includes(term));
      })
    : products;

  return (
    <div>
      <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
        <div className="font-heading text-2xl text-brand-navy">Products</div>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, SKU, barcode, category"
            data-testid="admin-product-search"
            className="h-10 border border-slate-200 rounded-lg px-3 text-sm w-64"
          />
          <button data-testid="admin-add-product-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-1 bg-brand-red text-white rounded-full h-10 px-4 font-bold active:scale-95">
            <Plus className="h-4 w-4" /> Add product
          </button>
        </div>
      </div>

      {(showNew || editing) && (
        <ProductForm product={editing} cats={cats}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={() => { setEditing(null); setShowNew(false); load(); }} />
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-xs uppercase tracking-wider text-slate-500">
                <th className="p-3">Image</th><th className="p-3">Product</th><th className="p-3">SKU</th>
                <th className="p-3">Category</th><th className="p-3">Condition</th><th className="p-3">Price</th>
                <th className="p-3">Available</th><th className="p-3">Status</th><th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={9} className="p-6 text-center text-slate-400">Loading...</td></tr> :
                shown.length === 0 ? <tr><td colSpan={9} className="p-6 text-center text-slate-400" data-testid="admin-products-empty">{term ? "No products match your search." : "No products yet."}</td></tr> :
                shown.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-t border-slate-100">
                    <td className="p-3"><img src={p.featured_image} className="h-10 w-10 rounded object-cover" alt="" /></td>
                    <td className="p-3 font-bold">{p.product_name}</td>
                    <td className="p-3 text-slate-500">{p.sku || <span className="text-brand-red font-bold inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />No SKU</span>}</td>
                    <td className="p-3">{cats.find((c) => c.id === p.category_id)?.name || "—"}</td>
                    <td className="p-3">{p.condition}</td>
                    <td className="p-3"><PriceCell p={p} onChanged={load} /></td>
                    <td className="p-3"><span className="font-bold">{p.quantity}</span> {p.inventory_status && <StatusPill status={p.inventory_status} />}</td>
                    <td className="p-3"><span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : p.status === "archived" ? "bg-slate-200 text-slate-600" : "bg-yellow-100 text-yellow-700"}`}>{p.status}</span></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(p)} data-testid={`admin-edit-${p.id}`} className="p-1.5 rounded hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setImagesFor(p)} data-testid={`admin-images-${p.id}`} className="p-1.5 rounded hover:bg-slate-100 text-brand-navy" title="Manage images"><Images className="h-4 w-4" /></button>
                        <button onClick={() => duplicate(p.id)} data-testid={`admin-duplicate-${p.id}`} className="p-1.5 rounded hover:bg-slate-100"><Copy className="h-4 w-4" /></button>
                        <button onClick={() => archive(p.id)} data-testid={`admin-archive-${p.id}`} className="p-1.5 rounded hover:bg-slate-100 text-brand-red"><Archive className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {imagesFor && <ProductImagesModal product={imagesFor} onClose={() => setImagesFor(null)} onSaved={load} />}
    </div>
  );
};

const PriceCell = ({ p, onChanged }) => {
  const onSale = p.sale_price != null && p.sale_price > 0 && p.sale_price < p.price;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(onSale ? String(p.sale_price) : "");
  const [saving, setSaving] = useState(false);
  const save = async (clear) => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${p.id}`, { sale_price: clear ? null : (val !== "" ? Number(val) : null) });
      toast.success(clear ? "Sale cleared" : "Sale set");
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update sale"); }
    setSaving(false);
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`sale-edit-${p.id}`}>
        <input type="number" min="0" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Now price" data-testid={`sale-input-${p.id}`} className="h-8 w-24 border border-slate-200 rounded px-2 text-sm" />
        <button onClick={() => save(false)} disabled={saving} data-testid={`sale-save-${p.id}`} className="h-8 px-2 rounded bg-brand-navy text-white text-xs font-bold">Save</button>
        {onSale && <button onClick={() => save(true)} disabled={saving} data-testid={`sale-clear-${p.id}`} className="h-8 px-2 rounded bg-slate-200 text-xs font-bold">Clear</button>}
        <button onClick={() => setEditing(false)} className="h-8 px-1 text-slate-400 text-xs">✕</button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5" data-testid={`price-cell-${p.id}`}>
      {onSale ? (
        <span className="inline-flex items-center gap-1.5 flex-wrap">
          <span className="font-bold text-brand-red">{money(p.sale_price)}</span>
          <span className="text-slate-400 line-through text-xs">{money(p.price)}</span>
          <span className="text-[9px] font-black uppercase bg-brand-red text-white rounded px-1.5 py-0.5" data-testid={`on-sale-marker-${p.id}`}>On sale</span>
        </span>
      ) : <span>{money(p.price)}</span>}
      <button onClick={() => { setVal(onSale ? String(p.sale_price) : ""); setEditing(true); }} data-testid={`set-sale-${p.id}`} className="text-[11px] text-brand-navy font-semibold hover:underline text-left w-fit">{onSale ? "Edit sale" : "Set sale"}</button>
    </div>
  );
};

const ProductForm = ({ product, cats, onClose, onSaved }) => {
  const [f, setF] = useState({
    product_name: product?.product_name || "",
    sku: product?.sku || "",
    barcode: product?.barcode || "",
    inventory_type: product?.inventory_type || "STANDARD",
    brand: product?.brand || "",
    category_id: product?.category_id || (cats[0]?.id || ""),
    condition: product?.condition || "NEW",
    condition_description: product?.condition_description || "",
    short_description: product?.short_description || "",
    description: product?.description || "",
    cost: product?.cost ?? 0,
    price: product?.price ?? 0,
    compare_at_price: product?.compare_at_price ?? "",
    sale_price: product?.sale_price ?? "",
    quantity: 1,
    reorder_level: 2,
    featured_image: product?.featured_image || "",
    status: product?.status || "published",
    is_featured: product?.is_featured || false,
    is_new_arrival: product?.is_new_arrival || false,
    is_clearance: product?.is_clearance || false,
    is_limited_quantity: product?.is_limited_quantity || false,
    badges: (product?.badges || []).join(", "),
  });
  const [invRow, setInvRow] = useState(null);
  const [invModal, setInvModal] = useState(null);
  const loadInv = () => product && api.get(`/inventory/product/${product.id}`).then((r) => setInvRow(r.data[0] || null));
  useEffect(() => { loadInv(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [product?.id]);

  const save = async () => {
    const payload = {
      ...f,
      sku: f.sku.trim() || undefined,
      cost: Number(f.cost),
      price: Number(f.price),
      compare_at_price: f.compare_at_price ? Number(f.compare_at_price) : null,
      sale_price: f.sale_price !== "" && f.sale_price != null ? Number(f.sale_price) : null,
      quantity: Number(f.quantity),
      reorder_level: Number(f.reorder_level),
      badges: f.badges.split(",").map((s) => s.trim()).filter(Boolean),
      images: f.featured_image ? [f.featured_image] : [],
    };
    if (product) { delete payload.quantity; delete payload.reorder_level; }
    try {
      if (product) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed"); }
  };

  const inv = invRow?.inventory;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 mt-10 space-y-3" data-testid="admin-product-form">
        <div className="flex justify-between items-center">
          <div className="font-heading text-xl text-brand-navy">{product ? "Edit product" : "New product"}</div>
          <button onClick={onClose} className="text-slate-500 hover:text-brand-red font-bold">Close</button>
        </div>
        <div className="text-brand-red font-black uppercase tracking-widest text-xs">Product information</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Name" value={f.product_name} onChange={(v) => setF({ ...f, product_name: v })} testId="pf-name" />
          <F label="Brand" value={f.brand} onChange={(v) => setF({ ...f, brand: v })} testId="pf-brand" />
          <F label={product ? "SKU" : "SKU (leave blank to auto-generate)"} value={f.sku} onChange={(v) => setF({ ...f, sku: v })} testId="pf-sku" />
          <F label="Barcode (UPC/EAN, optional)" value={f.barcode} onChange={(v) => setF({ ...f, barcode: v })} testId="pf-barcode" />
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Category</span>
            <select data-testid="pf-category" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })}
              className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white">
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Condition</span>
            <select data-testid="pf-condition" value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })}
              className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white">
              {["NEW", "OPEN BOX", "LIKE NEW", "USED", "RETURNED", "REFURBISHED", "CLEARANCE"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <F label="Cost (CAD, admin only)" type="number" value={f.cost} onChange={(v) => setF({ ...f, cost: v })} testId="pf-cost" />
          <F label="Regular price (CAD)" type="number" value={f.price} onChange={(v) => setF({ ...f, price: v })} testId="pf-price" />
          <F label="Sale price (CAD, optional — the 'now' price)" type="number" value={f.sale_price} onChange={(v) => setF({ ...f, sale_price: v })} testId="pf-sale" />
          <F label="Compare at Price (CAD, optional)" type="number" value={f.compare_at_price} onChange={(v) => setF({ ...f, compare_at_price: v })} testId="pf-compare" />
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Inventory type</span>
            <select data-testid="pf-inventory-type" value={f.inventory_type} onChange={(e) => setF({ ...f, inventory_type: e.target.value, quantity: e.target.value === "UNIQUE_ITEM" ? 1 : f.quantity })}
              className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white">
              <option value="STANDARD">STANDARD (identical units)</option><option value="UNIQUE_ITEM">UNIQUE ITEM (one-of-a-kind)</option>
            </select>
          </label>
          {!product && <F label="Initial quantity" type="number" value={f.quantity} onChange={(v) => setF({ ...f, quantity: v })} testId="pf-qty" />}
          {!product && <F label="Reorder level" type="number" value={f.reorder_level} onChange={(v) => setF({ ...f, reorder_level: v })} testId="pf-reorder" />}
          <F label="Featured image URL" value={f.featured_image} onChange={(v) => setF({ ...f, featured_image: v })} testId="pf-image" />
          <F label="Badges (comma separated)" value={f.badges} onChange={(v) => setF({ ...f, badges: v })} testId="pf-badges" />
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Status</span>
            <select data-testid="pf-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
              className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white">
              {["published", "draft", "archived"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
        </div>
        {(() => {
          const pv = priceView({ price: Number(f.price) || 0, sale_price: f.sale_price !== "" ? Number(f.sale_price) : null, compare_at_price: f.compare_at_price !== "" ? Number(f.compare_at_price) : null });
          return (
            <div className="border border-slate-200 rounded-xl p-3 bg-slate-50" data-testid="pf-price-preview">
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Shopper preview</div>
              <div className="flex items-center flex-wrap gap-2">
                <span className={`text-xl font-heading ${pv.onSale ? "text-brand-red" : "text-brand-navy"}`}>{money(pv.now)}</span>
                {pv.onSale && <span className="text-sm text-slate-500">Regular <span className="text-slate-400 line-through">{money(pv.regular)}</span></span>}
                {!pv.onSale && pv.was && pv.was > pv.now && <span className="text-sm text-slate-400 line-through">{money(pv.was)}</span>}
                {pv.pct ? <span className="text-xs font-black text-white bg-brand-red rounded-full px-2 py-0.5">-{pv.pct}%</span> : null}
                {pv.save ? <span className="text-xs font-black text-brand-red">SAVE {money(pv.save)}</span> : null}
                {pv.onSale && <span className="text-[10px] font-black uppercase bg-brand-red text-white rounded px-1.5 py-0.5">Sale</span>}
              </div>
              {pv.onSale && pv.retail && <div className="text-xs text-slate-500 mt-1">Retail <span className="line-through">{money(pv.retail)}</span></div>}
            </div>
          );
        })()}
        {product && (
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid="pf-inventory-section">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="text-brand-red font-black uppercase tracking-widest text-xs">Inventory information</div>
              {inv && <StatusPill status={inv.inventory_status} />}
            </div>
            {!inv ? <div className="text-sm text-slate-500 mt-2">No inventory record yet.</div> : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2 mt-3 text-center">
                  {[["On hand", inv.quantity_on_hand], ["Available", inv.quantity_available], ["Held", inv.quantity_held], ["Reserved", inv.quantity_reserved], ["Sold", inv.quantity_sold], ["Damaged", inv.quantity_damaged], ["Reorder", inv.reorder_level]].map(([l, v]) => (
                    <div key={l} className="bg-white rounded-lg p-2"><div className="text-[10px] uppercase font-black text-slate-500">{l}</div><div className="font-heading text-lg text-brand-navy">{v}</div></div>
                  ))}
                  <div className="bg-white rounded-lg p-2"><div className="text-[10px] uppercase font-black text-slate-500">Type</div><div className="font-bold text-xs mt-1">{inv.inventory_type === "UNIQUE_ITEM" ? "UNIQUE" : "STD"}</div></div>
                </div>
                <div className="text-xs text-slate-500 mt-2">Location: <b>{invRow.location_name}</b> · Storage: {["aisle", "rack", "shelf", "bin"].map((k) => inv.storage_location?.[k] ? `${k[0].toUpperCase() + k.slice(1)} ${inv.storage_location[k]}` : null).filter(Boolean).join(" · ") || "not set"}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setInvModal("adjust")} data-testid="pf-adjust-inventory-btn" className="bg-brand-red text-white rounded-full h-9 px-4 font-bold text-sm">Adjust inventory</button>
                  <button onClick={() => setInvModal("history")} data-testid="pf-view-history-btn" className="border border-slate-300 rounded-full h-9 px-4 font-bold text-sm">View history</button>
                </div>
              </>
            )}
          </div>
        )}
        <F label="Short description" value={f.short_description} onChange={(v) => setF({ ...f, short_description: v })} testId="pf-short" />
        <label className="block">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Full description</span>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4}
            data-testid="pf-description" className="mt-1 w-full rounded-lg border border-slate-200 p-2" />
        </label>
        <div className="flex flex-wrap gap-3 text-sm">
          {["is_featured", "is_new_arrival", "is_clearance", "is_limited_quantity"].map((k) => (
            <label key={k} className="inline-flex items-center gap-2">
              <input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`pf-${k}`} />
              <span>{k.replace("is_", "").replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={save} data-testid="pf-save-btn"
            className="bg-brand-navy text-white rounded-full px-6 h-11 font-bold">Save</button>
          <button onClick={onClose} className="rounded-full px-4 h-11 font-bold border border-slate-200">Cancel</button>
        </div>
        {invModal === "adjust" && invRow && <AdjustInventoryModal row={invRow} onClose={() => setInvModal(null)} onDone={() => { setInvModal(null); loadInv(); }} />}
        {invModal === "history" && invRow && <InventoryHistoryModal row={invRow} onClose={() => setInvModal(null)} />}
      </div>
    </div>
  );
};

const Categories = () => {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name) return;
    await api.post("/admin/categories", { name, image, display_order: cats.length });
    setName(""); setImage(""); toast.success("Category added"); load();
  };
  const disable = async (id) => { await api.delete(`/admin/categories/${id}`); toast.success("Disabled"); load(); };
  return (
    <div>
      <div className="font-heading text-2xl text-brand-navy mb-4">Categories</div>
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row gap-2 mb-4">
        <input data-testid="admin-cat-name" placeholder="Category name" value={name} onChange={(e) => setName(e.target.value)}
          className="flex-1 h-10 rounded-lg border border-slate-200 px-3" />
        <input data-testid="admin-cat-image" placeholder="Image URL (optional)" value={image} onChange={(e) => setImage(e.target.value)}
          className="flex-1 h-10 rounded-lg border border-slate-200 px-3" />
        <button data-testid="admin-cat-add" onClick={create} className="bg-brand-red text-white rounded-full h-10 px-5 font-bold">Add</button>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {cats.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`admin-cat-${c.slug}`}>
            {c.image && <img src={c.image} className="h-12 w-12 rounded object-cover" alt="" />}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500">/{c.slug}</div>
            </div>
            <button onClick={() => disable(c.id)} className="text-brand-red text-xs font-bold">Disable</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const F = ({ label, value, onChange, type = "text", testId }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3" />
  </label>
);
